import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Users,
  Search,
  Loader2,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Clock,
  Package,
  Ticket
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Client detail modal
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/clients?page=${page}&limit=20`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const res = await axios.get(url);
      setClients(res.data.clients || []);
      setTotal(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / 20));
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientHistory = async (email) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/history`);
      setClientHistory(res.data);
    } catch (error) {
      console.error("Error fetching client history:", error);
      toast.error("Failed to load client history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openClientDetail = async (client) => {
    setSelectedClient(client);
    setDetailDialogOpen(true);
    await fetchClientHistory(client.guest_email);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  return (
    <div data-testid="admin-clients">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Client Directory</h1>
        <p className="text-neutral-400">All guests who have attended Empire classes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-xl bg-empire-gold/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-empire-gold" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white">{total}</h3>
            <p className="text-sm text-neutral-400">Total Clients</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
          <Input
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="input-empire pl-10"
          />
        </div>
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
        </div>
      ) : clients.length === 0 ? (
        <Card className="card-empire">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
            <p className="text-neutral-500">No clients found</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="card-empire overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 bg-neutral-900/50">
                    <th className="text-left py-4 px-4 text-neutral-400 font-medium">Client</th>
                    <th className="text-left py-4 px-4 text-neutral-400 font-medium">Contact</th>
                    <th className="text-center py-4 px-4 text-neutral-400 font-medium">Bookings</th>
                    <th className="text-center py-4 px-4 text-neutral-400 font-medium">Attended</th>
                    <th className="text-left py-4 px-4 text-neutral-400 font-medium">First Booking</th>
                    <th className="text-left py-4 px-4 text-neutral-400 font-medium">Last Class</th>
                    <th className="text-center py-4 px-4 text-neutral-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client, index) => (
                    <motion.tr
                      key={client.guest_email}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b border-neutral-800/50 hover:bg-neutral-800/30"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-empire-gold/20 flex items-center justify-center">
                            <span className="text-empire-gold font-bold">
                              {client.guest_name?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <span className="text-white font-medium">{client.guest_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-1 text-neutral-400">
                            <Mail className="w-3 h-3" />
                            {client.guest_email}
                          </div>
                          <div className="flex items-center gap-1 text-neutral-500">
                            <Phone className="w-3 h-3" />
                            {client.guest_phone}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-white font-bold">{client.total_bookings}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {client.total_attended}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-neutral-400 text-sm">
                          {client.first_booking && format(parseISO(client.first_booking), "MMM d, yyyy")}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm">
                          <div className="text-white">{client.last_class_name || "-"}</div>
                          {client.last_attended_at && (
                            <div className="text-neutral-500 text-xs">
                              {format(parseISO(client.last_attended_at), "MMM d, yyyy")}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openClientDetail(client)}
                          className="text-empire-gold hover:text-empire-gold/80"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-neutral-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-neutral-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-neutral-700"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Client Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              {selectedClient && (
                <>
                  <div className="w-12 h-12 rounded-full bg-empire-gold/20 flex items-center justify-center">
                    <span className="text-empire-gold font-bold text-xl">
                      {selectedClient.guest_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-xl">{selectedClient.guest_name}</div>
                    <div className="text-sm text-neutral-400 font-normal">{selectedClient.guest_email}</div>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
            </div>
          ) : clientHistory ? (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-neutral-800/50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-white">{clientHistory.total_bookings}</div>
                  <div className="text-sm text-neutral-400">Total Bookings</div>
                </div>
                <div className="bg-neutral-800/50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-green-400">{clientHistory.total_attended}</div>
                  <div className="text-sm text-neutral-400">Classes Attended</div>
                </div>
              </div>

              {/* Booking History */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-empire-gold" />
                  Booking History
                </h4>
                
                {clientHistory.bookings && clientHistory.bookings.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {clientHistory.bookings.map((booking) => (
                      <div
                        key={booking.id}
                        className="bg-neutral-800/50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            booking.checked_in_at ? "bg-green-500" : "bg-neutral-500"
                          }`} />
                          <div>
                            <div className="text-white text-sm font-medium">
                              {booking.dance_class?.name || booking.booking_type}
                            </div>
                            <div className="text-xs text-neutral-500">
                              {booking.booking_number} • {booking.created_at && format(parseISO(booking.created_at), "MMM d, yyyy")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${
                            booking.checked_in_at
                              ? "bg-green-500/20 text-green-400"
                              : booking.status === "confirmed"
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-neutral-500/20 text-neutral-400"
                          }`}>
                            {booking.checked_in_at ? "Attended" : booking.status}
                          </Badge>
                          <span className="text-empire-gold text-sm font-medium">
                            KES {booking.total_amount?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 text-sm">No booking history</p>
                )}
              </div>

              {/* Package Purchases */}
              {clientHistory.packages && clientHistory.packages.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4 text-empire-gold" />
                    Package Purchases
                  </h4>
                  <div className="space-y-2">
                    {clientHistory.packages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className="bg-neutral-800/50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div>
                          <div className="text-white text-sm font-medium">{pkg.purchase_number}</div>
                          <div className="text-xs text-neutral-500">
                            {pkg.credits_remaining}/{pkg.credits_purchased} credits • Expires {pkg.expires_at && format(parseISO(pkg.expires_at), "MMM d, yyyy")}
                          </div>
                        </div>
                        <Badge className={`text-xs ${
                          pkg.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}>
                          {pkg.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
